"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

import CTAButton from "@/src/components/cta-button";
import { Button } from "@/src/components/shadcn/ui/button";
import { Label } from "@/src/components/shadcn/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/src/components/shadcn/ui/radio-group";
import { Textarea } from "@/src/components/shadcn/ui/textarea";
import { ToastAction } from "@/src/components/shadcn/ui/toast";
import { useTasksNavigation } from "@/src/hooks/use-tasks-navigation";
import { toast } from "@/src/hooks/use-toast";
import { useLocalTaskUpdateMutation, useVerifyAndCompleteMutation } from "@/src/lib/data-fetchers/tasks-fetchers";
import { type Task, TaskStatus } from "@/src/models/task";
import {
  VerificationReasonCode,
  VerificationResult,
  getVerificationReasonsForTaskType,
  VERIFICATION_REASON_LABELS,
} from "@/src/models/task-verification";

interface VerificationButtonsProps {
  task: Task;
  continueOnly?: boolean;
  disabled?: boolean;
  onComplete?: () => void;
  onVerificationStart?: () => void;
}

export function VerificationButtons({
  task,
  continueOnly = false,
  disabled,
  onVerificationStart,
}: VerificationButtonsProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();
  const localTaskUpdateMutation = useLocalTaskUpdateMutation({ invalidateOnSettled: false });
  const verifyAndCompleteMutation = useVerifyAndCompleteMutation({
    onError: () => {
      setIsSubmitting(false);
      toast({
        title: "Failed to verify and complete task",
        description: "Please refresh the page and try again.",
        variant: "destructive",
        action: task.displayId ? (
          <ToastAction altText="Go back to task" onClick={() => router.push(`/tasks/${task.displayId}`)}>
            Go back to task
          </ToastAction>
        ) : undefined,
      });
    },
  });
  const { navigate } = useTasksNavigation(task.displayId);

  const handleVerification = (
    verificationResult: VerificationResult,
    reason?: string | null,
    details?: string | null
  ) => {
    setIsSubmitting(true);

    onVerificationStart?.();

    // Update status locally only
    localTaskUpdateMutation.mutate({ ...task, status: TaskStatus.PROCESSING });

    // Request to update verification status and mark as completed. Tagged with
    // TASK_COMPLETION_MUTATION_KEY so the task list hides this task while the request is in
    // flight, instead of a poll landing before the write does and reverting the optimistic state.
    verifyAndCompleteMutation.mutate({ taskId: task.id, verificationResult, reason, details });

    // Navigate immediately to prevent flicker
    navigate();
  };

  const isBusy = disabled || isSubmitting || localTaskUpdateMutation.isPending;
  const reasonOptions = getVerificationReasonsForTaskType(task.type).map((r) => ({
    value: r,
    label: VERIFICATION_REASON_LABELS[r],
  }));
  const isPending = isSubmitting || localTaskUpdateMutation.isPending;

  if (continueOnly) {
    return (
      <div className="flex justify-center">
        <CTAButton
          small
          className="min-w-32 text-sm"
          onClick={() => handleVerification(VerificationResult.CORRECT, null)}
          disabled={disabled || isPending}
        >
          {isPending ? "Continuing..." : "Continue"}
        </CTAButton>
      </div>
    );
  }

  return (
    <div className="flex gap-8 w-full max-w-xl">
      <CTAButton
        variant="outline"
        onClick={() => handleVerification(VerificationResult.CORRECT, null)}
        disabled={isBusy}
      >
        {isPending ? "Updating..." : "Correct"}
      </CTAButton>
      {reasonOptions.length > 0 ? (
        <IncorrectReasonButton
          options={reasonOptions}
          disabled={isBusy}
          triggerLabel={isSubmitting || localTaskUpdateMutation.isPending ? "Updating..." : "Incorrect"}
          onSubmit={(reason, details) => handleVerification(VerificationResult.INCORRECT, reason, details)}
        />
      ) : (
        <CTAButton
          variant="destructive"
          onClick={() => handleVerification(VerificationResult.INCORRECT, null)}
          disabled={isBusy}
          focusOnCommandEnter={false}
        >
          {isSubmitting || localTaskUpdateMutation.isPending ? "Updating..." : "Incorrect"}
        </CTAButton>
      )}
    </div>
  );
}

interface IncorrectReasonButtonProps {
  options: { value: string; label: string }[];
  disabled: boolean;
  triggerLabel: string;
  onSubmit: (reason: string, details: string | null) => void;
}

function IncorrectReasonButton({ options, disabled, triggerLabel, onSubmit }: IncorrectReasonButtonProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [details, setDetails] = useState("");

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setReason(undefined);
      setDetails("");
    }

    setOpen(next);
  };

  const otherSelected = reason === VerificationReasonCode.OTHER;
  const canSave = Boolean(reason) && (!otherSelected || details.trim().length > 0);

  const handleSave = () => {
    if (!reason) {
      return;
    }

    onSubmit(reason, otherSelected ? details.trim() : null);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <CTAButton variant="destructive" disabled={disabled} focusOnCommandEnter={false} className="w-full p-6">
          {triggerLabel}
        </CTAButton>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-foreground">Why is this incorrect?</p>
          <RadioGroup value={reason} onValueChange={setReason} className="gap-1.5">
            {options.map((option) => (
              <div key={option.value} className="flex items-center gap-2">
                <RadioGroupItem value={option.value} id={`incorrect-reason-${option.value}`} />
                <Label htmlFor={`incorrect-reason-${option.value}`} className="text-sm font-normal text-foreground">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
          {otherSelected ? (
            <Textarea
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              placeholder="Describe the issue"
              className="bg-background"
            />
          ) : null}
          <div className="flex items-center justify-end">
            <Button type="button" size="sm" disabled={!canSave} onClick={handleSave} className="h-7 px-2">
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
